import { useRef, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Divider, Menu, MenuItem, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import HistoryIcon from '@mui/icons-material/History'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LogoutIcon from '@mui/icons-material/Logout'
import dayjs from 'dayjs'

import { ReconciliationSessionSummaryFragment } from '../../../../generated'
import { SaveStatus } from './useReconciliationSession'

const SAVE_LABEL: Record<SaveStatus, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'All changes saved',
  unsaved: 'Unsaved changes',
  error: 'Save failed'
}

const money = (n?: number | null) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * `updatedAt` is a timestamptz, so TypeORM hands back a `Date` while the field is exposed as a GraphQL String
 * — the String scalar coerces it via valueOf(), and it arrives as epoch MILLISECONDS ("1786446977083"), not
 * ISO. Passing that straight to dayjs reads the leading digits as a year and prints dates like "Oct 11, 1789".
 * Parse the number when that's what we got, while still accepting ISO if the field is ever changed.
 * (Same convention as the reconciliation history listing, which parseInt()s these fields.)
 */
const formatSavedAt = (value?: string | null): string => {
  if (!value) return '--'
  const epochMs = Number(value)
  const parsed = Number.isFinite(epochMs) ? dayjs(epochMs) : dayjs(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY · h:mm A') : '--'
}

const Stat = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
    <Typography variant="caption" color="text.secondary" noWrap lineHeight={1.4}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 400} lineHeight={1.4}>
      {value}
    </Typography>
  </Box>
)

/** Compact stat block for a session: balances and totals only. */
const SessionStats = ({
  session,
  interactive
}: {
  session: ReconciliationSessionSummaryFragment
  interactive?: boolean
}) => (
  <Box
    display="grid"
    gridTemplateColumns="1fr 1fr"
    rowGap={0.75}
    columnGap={3}
    mt={1}
    onClick={interactive ? undefined : e => e.stopPropagation()}
    sx={{ cursor: interactive ? 'pointer' : 'default' }}
  >
    <Stat label="Beginning Balance" value={money(session.beginningBalance)} />
    <Stat label="Statement Balance" value={money(session.statementBalance)} />
    <Stat label="Total Debits" value={money(session.debitTotal)} />
    <Stat label="Total Credits" value={money(session.creditTotal)} />
    <Stat label="Cleared Balance" value={money(session.clearedBalance)} />
    <Stat label="Amount to Clear" value={money(session.difference)} bold />
  </Box>
)

const SessionHeader = ({
  session,
  active
}: {
  session: ReconciliationSessionSummaryFragment
  active?: boolean
}) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
    <Typography variant="subtitle2" noWrap>
      {session.account?.sortCode ? `(#${session.account.sortCode}) ` : ''}
      {session.account?.name}
    </Typography>
    <Box display="flex" alignItems="center" gap={1}>
      <Chip size="small" label={dayjs(session.statementMonth).format('MMM YYYY')} />
      {active && <Chip size="small" color="primary" label="Active" />}
    </Box>
  </Box>
)

interface Props {
  sessions: ReconciliationSessionSummaryFragment[]
  activeSessionId: number | null
  hasActiveSession: boolean
  canStart: boolean
  /** True when a draft already exists for the bank on screen but isn't loaded (the user left it): the action
   *  resumes that draft rather than creating one, and must be labelled so. */
  canResume?: boolean
  saveStatus: SaveStatus
  onOpen: () => void
  onSelect: (session: ReconciliationSessionSummaryFragment) => void
  onStart: () => void
  onSaveNow: () => void
  /** Step out of the active session, keeping it open and resumable. */
  onLeave: () => void
  onDiscard: () => void
}

/**
 * Single header control for all reconciliation-session management: the active session's rich details +
 * Save/Discard, a Start action, and the list of other open sessions (one per bank) to resume.
 */
const ReconciliationSessionsMenu = ({
  sessions,
  activeSessionId,
  hasActiveSession,
  canStart,
  canResume,
  saveStatus,
  onOpen,
  onSelect,
  onStart,
  onSaveNow,
  onLeave,
  onDiscard
}: Props) => {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const activeSession = sessions.find(s => s.id === activeSessionId)
  const otherSessions = sessions.filter(s => s.id !== activeSessionId)

  const statusIcon = !hasActiveSession ? (
    <HistoryIcon fontSize="small" />
  ) : saveStatus === 'saving' ? (
    <CircularProgress size={14} color="inherit" />
  ) : saveStatus === 'saved' ? (
    <CheckCircleIcon fontSize="small" color="success" />
  ) : (
    <FiberManualRecordIcon fontSize="small" color={saveStatus === 'error' ? 'error' : 'warning'} />
  )

  const handleToggle = () => {
    if (!open) onOpen()
    setOpen(prev => !prev)
  }

  return (
    <>
      <Button
        ref={anchorRef}
        variant="outlined"
        color="primary"
        startIcon={statusIcon}
        endIcon={<ArrowDropDownIcon />}
        onClick={handleToggle}
      >
        Sessions{sessions.length ? ` (${sessions.length})` : ''}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 500, maxWidth: '90vw' } }}
      >
        {hasActiveSession && activeSession && (
          <Box px={2} pt={1.5} pb={1} sx={{ bgcolor: 'action.hover' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="overline" color="text.secondary">
                Current session
              </Typography>
              <Typography variant="caption" color={saveStatus === 'error' ? 'error.main' : 'text.secondary'}>
                {SAVE_LABEL[saveStatus] || 'Draft'}
              </Typography>
            </Box>
            <SessionHeader session={activeSession} active />
            <SessionStats session={activeSession} />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Last saved {formatSavedAt(activeSession.updatedAt)}
            </Typography>
            <Box display="flex" gap={1} mt={1.5}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                onClick={() => {
                  setOpen(false)
                  onSaveNow()
                }}
              >
                Save now
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => {
                  setOpen(false)
                  onLeave()
                }}
              >
                Leave session
              </Button>
              <Button
                size="small"
                variant="text"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setOpen(false)
                  onDiscard()
                }}
              >
                Discard
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Leaving saves and closes it here so you can work outside the draft. It stays open and resumable
              below. Discarding deletes it.
            </Typography>
          </Box>
        )}

        {!hasActiveSession && (
          <MenuItem
            disabled={!canStart}
            onClick={() => {
              setOpen(false)
              onStart()
            }}
          >
            <PlayCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            {!canStart
              ? 'Start session (select an account first)'
              : canResume
              ? 'Resume session for this bank'
              : 'Start session for this bank'}
          </MenuItem>
        )}

        <Divider />
        <Box px={2} pt={1} pb={0.5}>
          <Typography variant="overline" color="text.secondary">
            {hasActiveSession ? 'Other open sessions' : 'Open sessions'}
          </Typography>
        </Box>
        {otherSessions.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2">No other open sessions</Typography>
          </MenuItem>
        )}
        {otherSessions.map(session => (
          <MenuItem
            key={session.id}
            onClick={() => {
              setOpen(false)
              onSelect(session)
            }}
            sx={{ display: 'block', whiteSpace: 'normal', py: 1.5 }}
          >
            <SessionHeader session={session} />
            <SessionStats session={session} interactive />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Last saved {formatSavedAt(session.updatedAt)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default ReconciliationSessionsMenu
